import { create } from 'zustand';
import api from '../lib/api';
import { usePermissionsStore } from './permissions.store';
import { setSentryUser } from '../lib/sentry';
import { setInstantTasksUser } from '../lib/instant-tasks';
import { queryClient } from '../lib/query-client';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** RBAC role slugs from `user_roles` join — populated by login/refresh + GET /users/me. */
  roles?: string[];
  status: string;
  avatarUrl?: string;
  organizationId: string;
  timezone?: string | null;
  /** Resolved TZ from office → user → org chain (set by /users/me). */
  resolvedTimezone?: string | null;
  /** True when HR-created user must change password on first login. */
  mustChangePassword?: boolean;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  timezone?: string | null;
  currency?: string | null;
  /** MDY=1, DMY=2, YMD=3 — see DateFormat enum on the API */
  dateFormat?: number | null;
  /** TWELVE_HOUR=1, TWENTY_FOUR_HOUR=2 — see TimeFormat enum */
  timeFormat?: number | null;
  /** HH_MM=1, DECIMAL=2 — see DurationFormat enum */
  durationFormat?: number | null;
  /**
   * Free-form jsonb settings bag. Only sub-keys consumed by the web are typed
   * here; everything else is intentionally loose — read with care.
   */
  settings?: {
    financial?: {
      /** 1-based month number (1=Jan … 12=Dec) the org's fiscal year begins. */
      fiscal_year_start?: number;
    };
    [key: string]: unknown;
  } | null;
}

interface AuthState {
  user: User | null;
  organization: Organization | null;
  organizations: Organization[];
  isAuthenticated: boolean;
  isLoading: boolean;
  authChecked: boolean;
  suspended: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    website?: string;
  }) => Promise<{ email: string }>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  loadOrganizations: () => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  setSuspended: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  organizations: [],
  isAuthenticated: false,
  isLoading: false,
  authChecked: false,
  suspended: false,

  setSuspended: (value: boolean) => set({ suspended: value }),

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const loginRes = (await api.post('/auth/login', { email, password })) as { data?: { user: User } };
      // Tokens are set as HttpOnly cookies by the server — no localStorage needed
      // Interceptor strips { success } so loginRes = { data: { user, ... } }
      const loggedInUser = loginRes.data?.user ?? null;
      // Drop every cached query from any prior session in this tab. Without this,
      // tab-reused logins (HR logs out, junior logs in) see the previous user's
      // org-wide lists until they hard-refresh.
      queryClient.clear();
      set({ user: loggedInUser, isAuthenticated: true, isLoading: false });
      // The /auth/login payload is lean (id/email/name/roles/orgId only) — it omits
      // avatarUrl, designation, and the lineManager/supervisor/userGroup relations
      // the dashboard Profile card + sidebar avatar render. Enrich the store from
      // /users/me so those populate right after login instead of only after a manual
      // refresh (which is what previously triggered loadUser). Fire-and-forget so the
      // login→dashboard navigation isn't blocked; the store update re-renders the card.
      api
        .get('/users/me')
        .then((res) => {
          const full = (res as { data?: User }).data;
          // Guard the async result against a logout / different login that landed
          // in this tab while /users/me was in flight — only apply if we're still
          // authenticated as the same user, so we never resurrect a signed-out user.
          const cur = get();
          if (full && cur.isAuthenticated && cur.user?.id === full.id) {
            set({ user: full });
          }
        })
        .catch(() => {
          /* non-critical: card falls back to lean login fields until next refresh */
        });
      // Load org data immediately after login
      let orgIdForSentry: string | undefined;
      try {
        const orgRes = (await api.get('/organizations/current')) as { data: Organization };
        set({ organization: orgRes.data });
        orgIdForSentry = orgRes.data?.id;
      } catch { /* org fetch is non-critical during login */ }
      if (loggedInUser) {
        setSentryUser({ id: loggedInUser.id, email: loggedInUser.email, organizationId: orgIdForSentry });
        setInstantTasksUser({ id: loggedInUser.id, email: loggedInUser.email, organizationId: orgIdForSentry });
      }
      // Load user's organizations list
      get().loadOrganizations();
      // Load RBAC effective permissions for the org
      usePermissionsStore.getState().refresh();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (registerData) => {
    set({ isLoading: true });
    try {
      const registerRes = (await api.post('/auth/register', registerData)) as { data?: { email?: string } };
      set({ isLoading: false });
      // Register no longer returns tokens — user must verify email before login
      // Interceptor strips { success } so registerRes = { data: { email, ... } }
      return { email: registerRes.data?.email ?? registerData.email };
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  forgotPassword: async (email) => {
    await api.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token, password) => {
    await api.post('/auth/reset-password', { token, password });
  },

  logout: () => {
    // Server clears HttpOnly cookies on logout
    api.post('/auth/logout').catch(() => {});
    usePermissionsStore.getState().reset();
    queryClient.clear();
    setSentryUser(null);
    setInstantTasksUser(null);
    set({ user: null, organization: null, organizations: [], isAuthenticated: false, suspended: false });
  },

  loadUser: async () => {
    set({ isLoading: true });
    // Retry transient failures (network / 5xx) with exponential backoff so
    // a page reload mid-deploy (API container restarting) doesn't bounce the
    // user to /login while their HttpOnly cookies are still valid. Only a
    // genuine 401 ("session invalid") clears the auth state — every other
    // error keeps the spinner up and tries again.
    const maxAttempts = 5;
    let attempt = 0;
    while (attempt < maxAttempts) {
      try {
        const [userRes, orgRes] = (await Promise.all([
          api.get('/users/me'),
          api.get('/organizations/current'),
        ])) as [{ data: User }, { data: Organization }];
        set({
          user: userRes.data,
          organization: orgRes.data,
          isAuthenticated: true,
          isLoading: false,
          authChecked: true,
        });
        setSentryUser({
          id: userRes.data.id,
          email: userRes.data.email,
          organizationId: orgRes.data?.id,
        });
        setInstantTasksUser({
          id: userRes.data.id,
          email: userRes.data.email,
          organizationId: orgRes.data?.id,
        });
        get().loadOrganizations();
        usePermissionsStore.getState().refresh();
        return;
      } catch (err: any) {
        const status = err?.status ?? err?.response?.status ?? 0;
        // 401 = real auth rejection. Clear state and let the router send the
        // user to /login. The interceptor already attempted refresh once.
        if (status === 401) {
          usePermissionsStore.getState().reset();
          queryClient.clear();
          setSentryUser(null);
    setInstantTasksUser(null);
          set({ user: null, organization: null, organizations: [], isAuthenticated: false, isLoading: false, authChecked: true });
          return;
        }
        // Transient: network failure / 5xx. Back off and try again. Spinner
        // stays up (authChecked still false) so ProtectedRoute doesn't redirect.
        attempt++;
        if (attempt >= maxAttempts) break;
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    // All retries exhausted — server genuinely unreachable. Drop into the
    // logged-out state so the user can at least see the login page. The
    // subsequent login attempt either works (server back) or shows a clear
    // network error.
    usePermissionsStore.getState().reset();
    queryClient.clear();
    setSentryUser(null);
    setInstantTasksUser(null);
    set({ user: null, organization: null, organizations: [], isAuthenticated: false, isLoading: false, authChecked: true });
  },

  loadOrganizations: async () => {
    // Multi-org support removed (canonical-6 release) — user belongs to a
    // single org via users.organization_id. Org switcher is gone, but the
    // store keeps an `organizations` field for back-compat with components
    // that haven't been pruned yet. Always set to a single-element list.
    const { organization } = useAuthStore.getState();
    set({ organizations: organization ? [organization] : [] });
  },

  switchOrganization: async (_orgId: string) => {
    // No-op. Multi-org switcher removed — users have exactly one org now.
    return;
  },
}));
